import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface RiskRow {
  readonly riskScoreId: string;
  readonly riskType: string;
  readonly entityType: string;
  readonly score: string;
  readonly riskLevel: string;
  readonly confidenceScore: string;
  readonly status: string;
}

const RISK_LEVELS = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/**
 * M25 — Risk Dashboard (§11).
 * Lists computed risk scores across vendors, payments, claims, compliance,
 * audit and SLA, filterable by severity band. Each score is explainable and
 * advisory: it informs human risk decisions, it never blocks automatically.
 */
export function M25RiskDashboardPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RiskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [riskLevel, setRiskLevel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = riskLevel ? `?riskLevel=${encodeURIComponent(riskLevel)}` : '';
    apiRequest<{ data: RiskRow[]; total: number }>(`/v1/ai/risk-scores${query}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, riskLevel]);

  return (
    <main aria-label="risk-dashboard">
      <h1>{t('m25.risk.title')}</h1>
      <label htmlFor="risk-level-filter">{t('m25.risk.filterLabel')}</label>
      <select id="risk-level-filter" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
        {RISK_LEVELS.map((l) => <option key={l || 'all'} value={l}>{l || t('m25.risk.allLevels')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m25.risk.loading')}</p> : (
        <>
          <p aria-label="risk-count">{t('m25.risk.count', { count: total })}</p>
          <table aria-label="risk-table">
            <thead>
              <tr><th>{t('m25.risk.colType')}</th><th>{t('m25.risk.colEntity')}</th><th>{t('m25.risk.colScore')}</th><th>{t('m25.risk.colLevel')}</th><th>{t('m25.risk.colConfidence')}</th><th>{t('m25.risk.colStatus')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.riskScoreId}>
                  <td>{r.riskType}</td>
                  <td>{r.entityType}</td>
                  <td>{decimal(r.score)}</td>
                  <td>{r.riskLevel}</td>
                  <td>{decimal(r.confidenceScore)}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
